import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Megaphone, BarChart3, Users, Repeat, Workflow, TrendingUp, Eye, LayoutGrid, MessageSquare, Smartphone, Globe2, Zap, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCasesOfAgencyMessaging = [
  {
    title: "Bulk Campaign Delivery",
    description: "Send high-volume SMS, Viber, and RCS campaigns across multiple client accounts without delivery bottlenecks.",
    Icon: Megaphone,
    color: "text-amber",
    tags: ["SMS", "Viber", "RCS"],
  },
  {
    title: "Customer Engagement Tools",
    description: "Track opens, clicks, and replies to measure campaign performance for every client.",
    Icon: BarChart3,
    color: "text-sky",
    tags: ["SMS", "Viber", "RCS"],
  },
  {
    title: "Multi-Client Management",
    description: "Manage separate sender IDs and campaigns for each client from one platform.",
    Icon: Users,
    color: "text-lime",
    tags: ["SMS", "Viber"],
  },
  {
    title: "Automated Follow-Ups",
    description: "Trigger follow-up messages based on customer actions or campaign responses.",
    Icon: Repeat,
    color: "text-violet",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Cross-Channel Campaign Orchestration",
    description: "Coordinate SMS, Viber, and RCS campaigns from a single workflow.",
    Icon: Workflow,
    color: "text-navy",
    tags: ["SMS", "Viber", "RCS"],
  },
];

const enhancedBenefits = [
  {
    title: "Scale Campaigns Without Limits",
    description: "Carrier-grade infrastructure built to handle high-volume messaging across multiple client accounts.",
    Icon: TrendingUp,
    color: "text-amber",
  },
  {
    title: "Deliver Results Clients Can See",
    description: "Track delivery, engagement, and replies so you can prove campaign ROI to every client.",
    Icon: Eye,
    color: "text-sky",
  },
  {
    title: "Manage Every Client From One Place",
    description: "One platform for sender IDs, campaigns, reporting, and billing across your entire client portfolio.",
    Icon: LayoutGrid,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Reach every client's audience over the most universal channel.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Deliver interactive, media-rich campaign content.", Icon: Megaphone, gradient: "icon-tile-indigo" },
];

const faqs = [
  { question: "Can agencies manage multiple client accounts on Yootelco?", answer: "Yes. Yootelco supports separate sender IDs and campaign tracking so agencies can manage multiple clients from a single platform." },
  { question: "What channels can agencies use for campaigns?", answer: "Agencies can run campaigns over SMS and RCS, choosing the channel that fits each client's audience." },
  { question: "How does Yootelco handle high-volume sends?", answer: "Yootelco's direct carrier connections are built for high-volume, carrier-grade messaging, so bulk campaigns go out reliably." },
  { question: "Can I track campaign performance for clients?", answer: "Yes, delivery and engagement data is available so agencies can report results back to clients." },
  { question: "Is SMS marketing subject to consent requirements?", answer: "Yes, promotional SMS and RCS campaigns require prior recipient consent under most telecom and data protection regulations. Agencies should ensure client contact lists are properly opted in before sending." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const IndustriesDigitalMarketing = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS Campaign Tools for Digital Marketing Agencies – Yootelco"
        description="Run bulk SMS and RCS campaigns for your clients from one platform. Yootelco gives agencies reliable delivery and engagement tools."
        path="/industries/digital-marketing-agencies"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Scale Campaigns Across Every Client
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for Digital Marketing Agencies
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Deliver bulk SMS and RCS campaigns for multiple clients from one platform. Yootelco gives agencies the delivery reliability and engagement tools needed to run high-volume campaigns without switching providers.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                SMS
              </span>
              <span className="flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                RCS
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Agencies Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Agencies Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in the telecom industry, our team has the expertise to identify which campaigns are likely to succeed and which may face delivery or regulatory challenges. We understand country-specific regulations, operator requirements, and market restrictions, allowing us to optimize every campaign before it goes live.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Whether it's reducing costs, improving routing, or reshaping a campaign to comply with local regulations, we work proactively to maximize delivery rates while minimizing risks.
          </p>
        </div>
      </section>

      {/* Use Cases of Agency Messaging */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases of Agency Messaging</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCasesOfAgencyMessaging.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits for Agencies */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for Agencies</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          <div className="flex items-start gap-4 rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-indigo-500 to-violet-600 shadow-sm">
              <Globe2 className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="text-[16px] font-bold text-foreground">Direct connections to mobile network operators</h3>
            </div>
          </div>
          <div className="flex items-start gap-4 rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-violet-500 to-purple-600 shadow-sm">
              <Users className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="text-[16px] font-bold text-foreground">Multi-client sender ID management</h3>
            </div>
          </div>
          <div className="flex items-start gap-4 rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-400 to-teal-500 shadow-sm">
              <Zap className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <div>
              <h3 className="text-[16px] font-bold text-foreground">Multi-channel delivery across SMS and RCS</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 grid-balanced-2">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us about your client base, we'll show you how Yootelco can support your agency's campaign volume."
        useCase="Digital Marketing Agencies"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Scale Your Client Campaigns?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about running SMS and RCS campaigns for your clients.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesDigitalMarketing;
